import Fuse from "fuse.js";

/**
 * Sorting, grouping and fuzzy filtering for the property selector
 * (PropertySearchInput.vue) and the property list of the booking flow.
 *
 * Properties are grouped by their federal state (`state`, a free-text admin
 * field that may be empty). States and properties are sorted ascending using
 * German collation, and properties without a state form a trailing group
 * (`state: null`).
 */

const collator = new Intl.Collator("de");

/**
 * Normalizes text for matching so that "muenchen", "munchen" and "München"
 * can all find each other: lowercase, German umlauts/ß transliterated, any
 * remaining diacritics stripped.
 *
 * @param {*} value
 * @returns {string}
 */
export function normalize(value) {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value)
        .toLowerCase()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .trim();
}

/**
 * @param {Array<{name: string, state: ?string}>} properties
 * @returns {Array<{state: ?string, properties: Array}>} groups ordered by state
 *          (A–Z, the state-less group last), properties ordered by name (A–Z).
 */
export function groupPropertiesByState(properties) {
    const groups = new Map();

    properties.forEach((property) => {
        const state = (property.state || "").trim() || null;
        if (!groups.has(state)) {
            groups.set(state, []);
        }
        groups.get(state).push(property);
    });

    return Array.from(groups, ([state, items]) => ({
        state,
        properties: items.sort((a, b) => collator.compare(a.name, b.name)),
    })).sort((a, b) => {
        if (a.state === null) {
            return 1;
        }
        if (b.state === null) {
            return -1;
        }
        return collator.compare(a.state, b.state);
    });
}

/**
 * Same order as groupPropertiesByState(), as one flat list.
 *
 * @param {Array<{name: string, state: ?string}>} properties
 * @returns {Array}
 */
export function sortProperties(properties) {
    return groupPropertiesByState(properties).reduce(
        (sorted, group) => sorted.concat(group.properties),
        []
    );
}

/**
 * Builds a fuzzy matcher over the property name (weighted higher) and state.
 *
 * @param {Array<{name: string, state: ?string}>} properties
 * @returns {function(string): Array} returns the matching properties for a term.
 */
export function createPropertySearch(properties) {
    const fuse = new Fuse(properties, {
        keys: [
            { name: "name", weight: 2 },
            { name: "state", weight: 1 },
        ],
        threshold: 0.3,
        ignoreLocation: true,
        getFn: (property, path) =>
            normalize(Fuse.config.getFn(property, path)),
    });

    return (term) => fuse.search(normalize(term)).map((result) => result.item);
}
